/**
 * Share Visibility hook — Feb 2026.
 *
 * Reads the CEO-controlled ``share.visibility`` flags from the
 * existing public ``/api/share-cards/config`` endpoint and exposes
 * ``canShare(contentType)`` so any Share button can gate itself
 * without re-implementing the check. When a switch is OFF the
 * underlying content page still works — only Share is hidden.
 *
 * Defaults: every content type is shareable (safe rollout — a
 * missing/erroring config never blocks existing share flows).
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

const DEFAULTS = {
  tracks: true, playlists: true, contests: true, profiles: true,
  beats: true, reports: true, spotlight: true, winners: true,
};

let _cached = null;
let _inflight = null;

export const useShareVisibility = () => {
  const [state, setState] = useState(_cached || DEFAULTS);

  useEffect(() => {
    if (_cached) { setState(_cached); return; }
    if (!_inflight) {
      _inflight = api.get("/share-cards/config")
        .then((r) => {
          const v = r?.data?.visibility;
          _cached = v && typeof v === "object" ? { ...DEFAULTS, ...v } : DEFAULTS;
          return _cached;
        })
        .catch(() => { _cached = DEFAULTS; return DEFAULTS; })
        .finally(() => { _inflight = null; });
    }
    let cancelled = false;
    _inflight.then((v) => { if (!cancelled) setState(v); });
    return () => { cancelled = true; };
  }, []);

  const canShare = (contentType) => {
    if (!contentType) return true;
    return state[contentType] !== false;
  };

  return { canShare, visibility: state };
};

export default useShareVisibility;
